import type { BcStoreUserRecord } from '../services/api';
import type { PermissionMatrixRow } from './permissions-audit-matrix';
import type { PermissionMetricRow } from './permissions-audit-metrics';
import {
  type ColumnFilters,
  matchesAnySetFilter,
  matchesSetFilter,
  matrixGrantFilterValue,
  USER_NO_PERMISSIONS,
} from './permissions-audit-filters';

export function countUniqueAuditUserEmails(users: BcStoreUserRecord[]): number {
  const emails = users
    .map((user) => user.email.trim().toLowerCase())
    .filter((email) => email.length > 0);
  return new Set(emails).size;
}

export function filterMatrixRows(
  rows: PermissionMatrixRow[],
  permissionColumns: string[],
  filters: ColumnFilters,
): PermissionMatrixRow[] {
  return rows.filter((row) => {
    if (!matchesSetFilter(row.storeHash, filters.store_hash)) {
      return false;
    }
    if (!matchesSetFilter(row.storeName, filters.store_name)) {
      return false;
    }
    if (!matchesSetFilter(row.email, filters.email)) {
      return false;
    }
    if (!matchesSetFilter(row.status, filters.status)) {
      return false;
    }

    for (const code of permissionColumns) {
      const granted = row.cells[code] ?? false;
      if (!matchesSetFilter(matrixGrantFilterValue(granted), filters[code])) {
        return false;
      }
    }

    return true;
  });
}

export function filterAuditUsers(
  users: BcStoreUserRecord[],
  filters: ColumnFilters,
): BcStoreUserRecord[] {
  return users.filter((user) => {
    const fullName = `${user.firstName} ${user.lastName}`.trim();
    const permissionCodes = user.permissions.map((permission) => permission.code);

    if (!matchesSetFilter(user.storeHash, filters.store_hash)) {
      return false;
    }
    if (!matchesSetFilter(user.storeName, filters.store_name)) {
      return false;
    }
    if (!matchesSetFilter(user.email, filters.email)) {
      return false;
    }
    if (!matchesSetFilter(fullName, filters.name)) {
      return false;
    }
    if (!matchesSetFilter(user.status, filters.status)) {
      return false;
    }
    if (
      !matchesAnySetFilter(
        permissionCodes.length === 0 ? [USER_NO_PERMISSIONS] : permissionCodes,
        filters.permissions,
      )
    ) {
      return false;
    }

    return true;
  });
}

export function filterMetricRows(
  rows: PermissionMetricRow[],
  filters: ColumnFilters,
): PermissionMetricRow[] {
  return rows.filter((row) => {
    if (!matchesSetFilter(row.permissionCode, filters.permission)) {
      return false;
    }
    if (!matchesSetFilter(row.accessType, filters.type)) {
      return false;
    }
    if (!matchesSetFilter(String(row.userCount), filters.users)) {
      return false;
    }
    if (!matchesAnySetFilter(row.users, filters.whoHasIt)) {
      return false;
    }

    return true;
  });
}
